import React from 'react';
import { HomeIcon, ChartBarIcon, CogIcon, BanknotesIcon, DocumentCurrencyEuroIcon, DocumentTextIcon, ArrowTrendingUpIcon  } from '@heroicons/react/24/outline';
import { Link, useLocation } from 'react-router-dom';

type Props = { isOpen: boolean; onClose: () => void };

export default function Sidebar({ isOpen, onClose }: Props) {
  const { pathname } = useLocation();

  return (
    <>
      {/* Overlay mobile */}
      <div
        className={`lg:hidden ${isOpen ? 'fixed' : 'hidden'} inset-0 bg-black/40 z-40`}
        onClick={onClose}
      />

      {/* Drawer mobile */}
      <aside
        className={`lg:hidden fixed inset-y-0 left-0 z-50 w-64
        bg-white dark:bg-darkMode border-r border-gray-200 dark:border-dark
        transition-transform duration-300
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        h-[100dvh] overflow-y-auto`}
      >
        <Inner onClose={onClose} pathname={pathname} />
      </aside>

      {/* Desktop : sticky + scroll interne */}
      <aside
        className="
            hidden lg:block
            fixed top-0 left-0
            w-64 h-[100dvh]
            bg-white dark:bg-darkMode border-r border-gray-200 dark:border-dark
            z-[60] overflow-y-auto
        "
      >
        <Inner pathname={pathname} />
      </aside>
    </>
  );
}

function Inner({
  onClose,
  pathname,
}: {
  onClose?: () => void;
  pathname: string;
}) {
  return (
    <div className="flex flex-col min-h-full">
      {/* Header sticky dans la sidebar */}
      <div className="p-6 flex items-center justify-between sticky top-0 bg-white dark:bg-darkMode z-10 border-b border-gray-200/60 dark:border-dark/60">
        <div className="flex items-center gap-3">
          <img src="/logoAlizeeSoft.png" alt="Logo Alizée-Soft" className="h-8 w-auto" />
          <span className="text-2xl font-bold text-dark dark:text-white">Alizée-Soft</span>
        </div>
        {onClose && (
          <button onClick={onClose} className="lg:hidden text-gray-500">✕</button>
        )}
      </div>

      {/* Le menu prend la hauteur restante et peut scroller si besoin */}
      <nav className="p-4 space-y-1 dark:text-darkGray flex-1">
        <NavItem to="/dashboard" icon={<HomeIcon className="w-5 h-5" />} active={pathname === '/dashboard'}>
          Accueil
        </NavItem>
        <NavItem to="/stats" icon={<ChartBarIcon className="w-5 h-5" />} active={pathname === '/stats'}>
          Statistiques
        </NavItem>
        <NavItem to="/dynamicChartPage" icon={<ArrowTrendingUpIcon className="w-5 h-5" />} active={pathname === '/dynamicChartPage'}>
          Graphique évolution 
        </NavItem>
        <NavItem to="/factures" icon={<DocumentCurrencyEuroIcon className="w-5 h-5" />} active={pathname === '/factures'}>
          Factures
        </NavItem>
        <NavItem to="/fiche-sejour" icon={<DocumentTextIcon className="w-5 h-5" />} active={pathname === '/fiche-sejour'}>
          Fiches Séjour
        </NavItem>
        <NavItem to="/settings" icon={<CogIcon className="w-5 h-5" />} active={pathname === '/settings'}>
        Paramètres
      </NavItem>
        {/* … ajoute d’autres items, la zone scrolle sans bouger le header */}
      </nav>
    </div>
  );
}

function NavItem({
  to,
  icon,
  active,
  children,
}: {
  to: string;
  icon: React.ReactNode;
  active?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      className={[
        'flex items-center gap-3 rounded px-4 py-2 transition',
        active
          ? 'bg-blue-50 text-blue-700 dark:bg-white/10 dark:text-white'
          : 'text-gray-700 hover:bg-blue-100 dark:text-darkGray dark:hover:bg-dark',
      ].join(' ')}
    >
      {icon}
      <span className="truncate">{children}</span>
    </Link>
  );
}
